import classes from './ForgeAccess.module.css';
import { clientIdentity } from '@/context/ApiClientContext/identity';
import { useState } from 'react';
import { Alert, Container, Group, Stack, Text, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useApiClient } from '@/context/ApiClientContext/useApiClient';
import StyledButton from '@/components/StyledButton/StyledButton';

export default function ForgeConnectPage() {
  const api = useApiClient();
  const [params] = useSearchParams();
  const id = params.get('request');
  return <ConnectionDecision key={`${clientIdentity(api)}:${id}`} id={id} />;
}

function ConnectionDecision({ id }: { id: string | null }) {
  const api = useApiClient();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const options = { suppressGlobalErrorToast: true, handleForbiddenLocally: true };
  const query = useQuery({
    meta: { suppressGlobalErrorToast: true },
    queryKey: ['forge-connect', id, clientIdentity(api)],
    enabled: Boolean(id),
    retry: false,
    queryFn: async () =>
      (await api.get(`/internal/forge-auth/requests/${encodeURIComponent(id!)}`, options)).data,
  });
  const state = result || query.data?.state;
  async function decide(decision: 'approve' | 'deny') {
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post(
        `/internal/forge-auth/requests/${encodeURIComponent(id!)}/decision`,
        { decision },
        options,
      );
      setResult(data.state);
    } catch {
      setError('The decision was not confirmed. Reload this page to check its current state.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <Container size="sm" py="xl">
      <Stack>
        <Title order={1}>Connect Forge</Title>
        <Text>
          Only approve a connection you started on your own computer. Check that the confirmation
          code matches your terminal.
        </Text>
        {!id && <Alert color="red">Missing login request. Start login from the Forge CLI.</Alert>}
        {query.isPending && id && <Text>Checking connection…</Text>}
        {query.error && (
          <Alert color="red">
            This request is unavailable, expired, or requires an internal administrator account.
          </Alert>
        )}
        {query.data && (
          <>
            <Text>Account: {query.data.email}</Text>
            <Text>Environment: {query.data.environment}</Text>
            <Text>Organization: {query.data.org_name || 'Organization unavailable'}</Text>
            <Text fw={700} size="xl">
              {query.data.confirmation_code}
            </Text>
            <Text>
              This connection expires after 30 days. Automation permissions are managed separately
              and can be revoked immediately.
            </Text>
            {state === 'pending' ? (
              <Group>
                <StyledButton
                  variant="primary"
                  loading={busy}
                  disabled={Boolean(error)}
                  onClick={() => void decide('approve')}
                >
                  Approve connection
                </StyledButton>
                <StyledButton
                  variant="outline"
                  className={classes.secondary}
                  disabled={busy || Boolean(error)}
                  onClick={() => void decide('deny')}
                >
                  Deny
                </StyledButton>
              </Group>
            ) : (
              <Alert color={state === 'approved' || state === 'consumed' ? 'green' : 'gray'}>
                Connection {state}. Return to your terminal.
              </Alert>
            )}
          </>
        )}
        {error && <Alert color="red">{error}</Alert>}
      </Stack>
    </Container>
  );
}
